// Shared catalog of taggable interests for onboarding + profile editing.
// Stored on profiles.tags as TEXT[]. Kept lowercase so we can match against
// scans.tags / scans.category / scans.brand without case headaches.

export interface Interest {
  /** Storage value — what we save to profiles.tags. Lowercase, stable. */
  value: string;
  /** Human label shown in the menu. */
  label: string;
  /** Loose grouping for the menu UI. */
  group: "Categories" | "Eras" | "Aesthetics" | "Regions";
}

export const INTERESTS: Interest[] = [
  // ── Categories ────────────────────────────────────────────────
  { value: "denim", label: "Denim", group: "Categories" },
  { value: "workwear", label: "Workwear", group: "Categories" },
  { value: "outerwear", label: "Outerwear", group: "Categories" },
  { value: "knitwear", label: "Knitwear", group: "Categories" },
  { value: "tees", label: "Tees & Tops", group: "Categories" },
  { value: "shirts", label: "Button-ups", group: "Categories" },
  { value: "footwear", label: "Footwear", group: "Categories" },
  { value: "bags", label: "Bags", group: "Categories" },
  { value: "suiting", label: "Suiting", group: "Categories" },
  { value: "athletic", label: "Athletic", group: "Categories" },
  { value: "dresses", label: "Dresses", group: "Categories" },
  { value: "skirts", label: "Skirts", group: "Categories" },
  { value: "lingerie", label: "Lingerie", group: "Categories" },
  { value: "accessories", label: "Accessories", group: "Categories" },
  { value: "jewelry", label: "Jewelry", group: "Categories" },
  { value: "hats", label: "Hats", group: "Categories" },
  { value: "hawaiian", label: "Hawaiian shirts", group: "Categories" },
  { value: "band-tees", label: "Band tees", group: "Categories" },

  // ── Eras ──────────────────────────────────────────────────────
  { value: "50s", label: "50s", group: "Eras" },
  { value: "60s", label: "60s", group: "Eras" },
  { value: "70s", label: "70s", group: "Eras" },
  { value: "80s", label: "80s", group: "Eras" },
  { value: "90s", label: "90s", group: "Eras" },
  { value: "y2k", label: "Y2K", group: "Eras" },
  { value: "2010s", label: "2010s archive", group: "Eras" },

  // ── Aesthetics ────────────────────────────────────────────────
  { value: "rock-tees", label: "Rock tees", group: "Aesthetics" },
  { value: "americana", label: "Americana", group: "Aesthetics" },
  { value: "military", label: "Military surplus", group: "Aesthetics" },
  { value: "prep", label: "Prep / Ivy", group: "Aesthetics" },
  { value: "designer-archive", label: "Designer archive", group: "Aesthetics" },
  { value: "luxury", label: "Luxury houses", group: "Aesthetics" },
  { value: "streetwear", label: "Streetwear", group: "Aesthetics" },
  { value: "skate", label: "Skate / Surf", group: "Aesthetics" },
  { value: "outdoor", label: "Outdoor / Gorpcore", group: "Aesthetics" },
  { value: "western", label: "Western", group: "Aesthetics" },
  { value: "leather", label: "Leather", group: "Aesthetics" },
  { value: "punk", label: "Punk", group: "Aesthetics" },
  { value: "goth", label: "Goth / Alt", group: "Aesthetics" },
  { value: "grunge", label: "Grunge", group: "Aesthetics" },
  { value: "hip-hop", label: "Hip-hop / Rap", group: "Aesthetics" },
  { value: "boho", label: "Boho / 70s flow", group: "Aesthetics" },
  { value: "minimalist", label: "Minimalist", group: "Aesthetics" },
  { value: "avant-garde", label: "Avant-garde", group: "Aesthetics" },
  { value: "mod", label: "Mod / 60s", group: "Aesthetics" },
  { value: "rockabilly", label: "Rockabilly", group: "Aesthetics" },
  { value: "athleisure", label: "Vintage athletic", group: "Aesthetics" },
  { value: "preppy-coastal", label: "Coastal / Nautical", group: "Aesthetics" },
  { value: "art-deco", label: "Art deco / 20s–40s", group: "Aesthetics" },
  { value: "techwear", label: "Techwear", group: "Aesthetics" },

  // ── Regions ───────────────────────────────────────────────────
  { value: "japanese", label: "Archive Japanese", group: "Regions" },
  { value: "italian", label: "Italian", group: "Regions" },
  { value: "british", label: "British", group: "Regions" },
  { value: "french", label: "French", group: "Regions" },
  { value: "american", label: "American heritage", group: "Regions" },
  { value: "scandi", label: "Scandinavian", group: "Regions" },
  { value: "korean", label: "Korean", group: "Regions" },
];

export const MIN_INTERESTS = 3;
export const MAX_INTERESTS = 10;

/** Normalises arbitrary tag input to a known interest value, or returns it unchanged. */
export function normaliseInterest(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, "-");
}
